import { useEffect, useRef } from "react";

interface AdUnitProps {
  /** Slot ID de AdSense (se obtiene del bloque de anuncios creado en AdSense) */
  slot: string;
  /** Formato del anuncio: "auto" (por defecto), "horizontal", "rectangle", "vertical" */
  format?: "auto" | "horizontal" | "rectangle" | "vertical";
  /** Clase CSS adicional para el contenedor */
  className?: string;
  /** Etiqueta opcional que describe el espacio (ej: "Publicidad") */
  label?: string;
}

const FORMAT_CLASSES: Record<string, string> = {
  auto: "",
  horizontal: "ad-format-horizontal",
  rectangle: "ad-format-rectangle",
  vertical: "ad-format-vertical",
};

/**
 * Componente reutilizable para mostrar anuncios de AdSense.
 * Se integra visualmente con el diseño oscuro del portfolio y
 * se coloca en lugares estratégicos sin ser intrusivo.
 *
 * Uso:
 *   <AdUnit slot="6805590889" format="horizontal" />
 */
export default function AdUnit({
  slot,
  format = "auto",
  className = "",
  label,
}: AdUnitProps) {
  const insRef = useRef<HTMLModElement>(null);
  const pushedRef = useRef(false);

  useEffect(() => {
    // Evitar doble push en StrictMode (React 18)
    if (pushedRef.current) return;
    pushedRef.current = true;

    try {
      // Forzar width/height automáticos antes del push
      if (insRef.current) {
        insRef.current.style.width = "";
        insRef.current.style.height = "";
      }
      ((window as any).adsbygoogle = (window as any).adsbygoogle || []).push({});
    } catch (e) {
      // El error es esperado en desarrollo o cuando hay bloqueadores
      if (process.env.NODE_ENV === "development") {
        console.warn("AdSense push error (esperado en dev):", e);
      }
    }
  }, []);

  const formatClass = FORMAT_CLASSES[format] || "";

  return (
    <div
      className={`ad-unit-wrapper w-full my-6 ${className}`}
      role="complementary"
      aria-label={label || "Publicidad"}
    >
      {label && (
        <p className="text-center text-[10px] tracking-widest uppercase text-gray-600 mb-2">
          {label}
        </p>
      )}
      <div className={`ad-unit-inner mx-auto overflow-hidden ${formatClass}`}>
        <ins
          ref={insRef}
          className="adsbygoogle"
          style={{ display: "block" }}
          data-ad-client="ca-pub-1816943561296946"
          data-ad-slot={slot}
          data-ad-format={format === "auto" ? "auto" : "rectangle"}
          data-full-width-responsive="true"
        />
      </div>
    </div>
  );
}
